import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { scheduledLessonsQueryKeys } from '@/data/services/querykeys';
import User from '@/models/User';
import { getErrorMessage } from '@/utils/getErrorMessage';
import SimpleScheduledLessonCard from './SimpleScheduledLessonCard';
import useYourLessonsFilters, { YourLessonsTab } from './useYourLessonsFilters';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import useKlassesMap from '@/data/hook/shared/useKlassesMap';
import { Loading, Tabs } from '../ui/daisy';

type Props = {
  user?: User;
};

export default function YourLessons(props: Props) {
  const { user } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'teacherDashboard.yourLessons',
  });

  const [tab, setTab] = useState<YourLessonsTab>('today');
  const { filter } = useYourLessonsFilters({ user, tab });

  const {
    results: scheduledLessons,
    isInitialLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
  } = useInfiniteService({
    ...scheduledLessonsQueryKeys.list({
      ...filter,
      pageSize: 7,
    })._ctx.infinity,
    staleTime,
    retry: false,
  });

  const klassIds = scheduledLessons.map(lesson => lesson.klass);
  const { klassesMap, isLoading: loadingKlasses } = useKlassesMap({ klassIds });

  return (
    <div className="overflow-hidden h-full p-4 bg-base-100 border border-base-300 rounded-2xl">
      <aside className="w-fit">
        <Tabs
          variant="box"
          value={tab}
          onChange={value => setTab(value as YourLessonsTab)}
        >
          <Tabs.Tab value="today">{t('today')}</Tabs.Tab>
          <Tabs.Tab value="nextLessons">{t('nextLessons')}</Tabs.Tab>
        </Tabs>
      </aside>
      <InfinityList
        hasNextPage={hasNextPage && !loadingKlasses}
        isFetchingNextPage={isFetchingNextPage}
        onReachEnd={fetchNextPage}
        className="gap-2 scrollbar-none grow min-h-[168px] max-h-79 py-4 w-full"
        scroll
      >
        <ConditionalRenderer condition={isInitialLoading}>
          <div className="flex w-full h-full justify-center">
            <Loading color="primary" size="sm" />
          </div>
        </ConditionalRenderer>

        <ConditionalRenderer condition={error}>
          <div className="flex w-full h-full justify-center">
            <Text format="rubik-400" text={getErrorMessage(error)} />
          </div>
        </ConditionalRenderer>

        {scheduledLessons.map(scheduledLesson => (
          <SimpleScheduledLessonCard
            key={scheduledLesson.id}
            scheduledLesson={scheduledLesson}
            klass={klassesMap[scheduledLesson.klass]}
            isLoading={loadingKlasses && !klassesMap[scheduledLesson.klass]}
          />
        ))}

        <ConditionalRenderer
          condition={!scheduledLessons.length && !isInitialLoading && !error}
        >
          <div className="w-full h-full flex justify-center items-center">
            <span className="text-sm text-base-content/60">{t('empty')}</span>
          </div>
        </ConditionalRenderer>
      </InfinityList>
    </div>
  );
}
